/**
 * Dictation Service
 * Sends recorded audio to the backend Whisper transcription endpoint
 * and returns the transcribed text.
 */

import { IPublicClientApplication } from '@azure/msal-browser';
import { getAccessTokenHelper } from '../auth/msalAuth';
import logger from '../logger/logger';

const TRANSCRIPTION_ENDPOINT = '/transcription';
const REQUEST_TIMEOUT_MS = 30_000;
/** Base timeout for any transcription request (covers small recordings + backend latency). */
const BASE_TIMEOUT_MS = 30_000;
/** Additional timeout per MB of audio (accounts for upload time on slower networks). */
const TIMEOUT_PER_MB_MS = 15_000;

class DictationService {
  private msalInstance: IPublicClientApplication | null = null;

  setMsalInstance(msalInstance: IPublicClientApplication): void {
    this.msalInstance = msalInstance;
  }

  /**
   * Transcribe an audio blob using the backend Whisper endpoint.
   *
   * @param audioBlob - WAV/MP3/OGG/WEBM audio blob
   * @param language  - Optional ISO-639-1 language code (e.g. "en")
   * @returns Transcribed text string
   */
  async transcribe(audioBlob: Blob, language?: string): Promise<string> {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not set. Call setMsalInstance() first.');
    }

    const token = await getAccessTokenHelper(this.msalInstance);

    const baseUrl = import.meta.env.VITE_APIM_BASE_URL || 'https://navigatordev.ai.3m.com';
    const useLocal = import.meta.env.VITE_USE_LOCAL_DICTATION === 'true';
    const url = useLocal
      ? `http://localhost:8000${TRANSCRIPTION_ENDPOINT}`
      : `${baseUrl}/nav-api${TRANSCRIPTION_ENDPOINT}`;

    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.wav');
    if (language) {
      formData.append('language', language);
    }

    const controller = new AbortController();
    const timeoutMs = BASE_TIMEOUT_MS + (audioBlob.size / (1024 * 1024)) * TIMEOUT_PER_MB_MS;
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    try {
      logger.log(`[DictationService] Sending ${audioBlob.size} bytes to ${url}`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        const retryAfter = response.headers.get('Retry-After');
        const err = new Error(`Transcription failed (${response.status}): ${errorText}`) as any;
        err.status = response.status;
        if (retryAfter) {
          err.retryAfterSeconds = parseInt(retryAfter, 10) || 60;
        }
        throw err;
      }

      const result = await response.json();
      const text = result.text || '';
      logger.log(`[DictationService] Transcription result: ${text.length} chars`);
      return text;
    } catch (err: any) {
      if (err.name === 'AbortError') {
        throw new Error('Transcription request timed out. Please try again.');
      }
      throw err;
    } finally {
      clearTimeout(timeout);
    }
  }
}

export const dictationService = new DictationService();
